// The undo bar. EXTRACTED from RequestsInboxPage.js at Session 79.14d for
// #304's D10 — not written from scratch, which is a correction the gate makes
// to its own cost estimate: it had priced this as a build that could be dropped
// if the session ran long, and the Requests inbox already had a complete one
// with an undo action, a five-second timer and its own mobile position.
//
// WHY #304 NEEDS IT. Closing the last outstanding line settles the order, and a
// settled order leaves the Purchasing Queue by design. The only thing on screen
// saying anything happened is a count quietly dropping from 7 to 6 — gate
// callout M — and a number decreasing is not feedback. The search this gate
// builds answers the considered case, the vendor ringing on Tuesday; the undo
// bar answers the slip, ten seconds ago, in one tap.
//
// `position: fixed` is deliberate and is NOT a locked-rule-3 violation. #148's
// own scope says so in as many words — "RequestsInboxPage.js (3 overlays; its
// Snackbar is not one)". It is a transient bar, not an overlay: it traps no
// focus, covers nothing, and takes no dismiss-on-backdrop.

import React, { useCallback, useEffect, useRef, useState } from 'react';

export default function Snackbar({ msg, undoFn, onDismiss, isMobile }) {
  if (!msg) return null;
  return (
    <div
      // Announced rather than silent: the bar is the only confirmation that
      // anything happened, so a screen reader that never sees it gets no
      // feedback at all. `status` is polite — it waits for a pause rather than
      // cutting across whatever is being read.
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: isMobile ? 80 : 24,
        left: isMobile ? 12 : '50%',
        right: isMobile ? 12 : 'auto',
        transform: isMobile ? 'none' : 'translateX(-50%)',
        background: '#1a1a1a', color: 'white',
        borderRadius: 10, padding: '12px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 20, zIndex: 3000, minWidth: isMobile ? 'auto' : 280,
      }}
    >
      <span style={{ fontSize: 13 }}>{msg}</span>
      {undoFn && (
        <button
          onClick={() => { undoFn(); onDismiss(); }}
          style={{
            background: 'none', border: 'none',
            color: '#7dd3b8', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            // #513's 44px touch minimum is app-wide work carved out for Session
            // 80, but this control is the whole reason the bar exists and it is
            // on a five-second clock — a miss here is not recoverable by trying
            // again. Padding rather than a height, so the bar does not grow.
            padding: '8px 10px', margin: '-8px -10px',
          }}
        >
          Undo
        </button>
      )}
    </div>
  );
}

// The timer half, so a second caller cannot get it subtly wrong. Both of the
// things that are easy to miss are in here: clearing a running timer before
// starting a new one, so two quick actions do not leave the first one's
// callback to dismiss the second one's bar; and clearing on unmount, so a
// timer cannot fire setState against a page that has gone.
export function useSnackbar(seconds = 5) {
  const [snackbar, setSnackbar] = useState(null);
  const timer = useRef(null);

  const dismiss = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    setSnackbar(null);
  }, []);

  const showSnackbar = useCallback((msg, undoFn) => {
    if (timer.current) clearTimeout(timer.current);
    setSnackbar({ msg, undoFn });
    timer.current = setTimeout(() => setSnackbar(null), seconds * 1000);
  }, [seconds]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return { snackbar, showSnackbar, dismiss };
}
